import { CSSProperties, ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Flex.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Flex-specific props that extend layout utilities
 * @extends Omit<LayoutUtilProps, "flexBasis" | "flexGrow" | "flexDirection" | "flexWrap" | "flexShrink">
 */
type FlexSpecificProps = Omit<
  LayoutUtilProps,
  "flexBasis" | "flexGrow" | "flexDirection" | "flexWrap" | "flexShrink"
> & {
  /**
   * Applies `display: inline-flex` instead of `flex`
   */
  inline?: boolean;
  /**
   * The flex direction of the container.
   */
  direction?: CSSProperties["flexDirection"];
  /**
   * The flex grow value for the container.
   */
  grow?: CSSProperties["flexGrow"];
  /**
   * The flex shrink value for the container.
   */
  shrink?: CSSProperties["flexShrink"];
  /**
   * The flex basis value for the container.
   */
  basis?: CSSProperties["flexBasis"];
  /**
   * The flex wrap value for the container.
   */
  wrap?: CSSProperties["flexWrap"];

  /** @deprecated use shrink prop instead */
  flexShrink?: CSSProperties["flexShrink"];
};

/**
 * Props for the Flex component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends FlexSpecificProps
 */
export type FlexProps = ComponentPropsWithoutRef<"div"> &
  FlexSpecificProps & {
    /**
     * Responsive props for small screens (sm breakpoint).
     */
    sm?: FlexSpecificProps;
    /**
     * Responsive props for medium screens (md breakpoint).
     */
    md?: FlexSpecificProps;
    /**
     * Responsive props for large screens (lg breakpoint).
     */
    lg?: FlexSpecificProps;
    /**
     * Responsive props for extra large screens (xl breakpoint).
     */
    xl?: FlexSpecificProps;
    /**
     * Responsive props for extra extra large screens (xxl breakpoint).
     */
    xxl?: FlexSpecificProps;
  };

/**
 * Flex component for creating flexible layouts using CSS Flexbox.
 *
 * Features:
 * - CSS Flexbox layout with full control over flex properties
 * - Support for both flex and inline-flex display modes
 * - Responsive design with breakpoint-specific props (sm, md, lg, xl, xxl)
 * - Comprehensive layout utilities for spacing, alignment, and positioning
 * - Automatic style generation and class management
 * - Support for all standard div element props
 * - Flexible direction, grow, shrink, basis, and wrap controls
 * - Deprecated flexShrink prop with modern shrink alternative
 *
 * @example
 * <Flex direction="row" gap="4" alignItems="center">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </Flex>
 *
 * @example
 * <Flex
 *   direction="column"
 *   gap="2"
 *   md={{ direction: "row", gap: "4" }}
 * >
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </Flex>
 */
export const Flex = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
  const {
    inline,
    direction,
    grow,
    shrink,
    basis,
    wrap,
    sm,
    md,
    lg,
    xl,
    xxl,
    ...nonLayoutProps
  } = props;
  const combineLayoutProps = {
    ...nonLayoutProps,
    flexDirection: direction,
    flexGrow: grow,
    flexShrink: shrink,
    flexBasis: basis,
    flexWrap: wrap,
    sm: {
      ...sm,
      flexDirection: sm?.direction,
      flexGrow: sm?.grow,
      flexShrink: sm?.shrink,
      flexBasis: sm?.basis,
      flexWrap: sm?.wrap,
    },
    md: {
      ...md,
      flexDirection: md?.direction,
      flexGrow: md?.grow,
      flexShrink: md?.shrink,
      flexBasis: md?.basis,
      flexWrap: md?.wrap,
    },
    lg: {
      ...lg,
      flexDirection: lg?.direction,
      flexGrow: lg?.grow,
      flexShrink: lg?.shrink,
      flexBasis: lg?.basis,
      flexWrap: lg?.wrap,
    },
    xl: {
      ...xl,
      flexDirection: xl?.direction,
      flexGrow: xl?.grow,
      flexShrink: xl?.shrink,
      flexBasis: xl?.basis,
      flexWrap: xl?.wrap,
    },
    xxl: {
      ...xxl,
      flexDirection: xxl?.direction,
      flexGrow: xxl?.grow,
      flexShrink: xxl?.shrink,
      flexBasis: xxl?.basis,
      flexWrap: xxl?.wrap,
    },
  };
  const { layoutStyles, componentProps } =
    useLayoutPropsUtil(combineLayoutProps);

  const { children, className, style, ...rest } = componentProps;

  const flexClassNames = cx(className, {
    [styles["flex"]]: !inline,
    [styles["inline-flex"]]: inline,
  });

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <div
      className={flexClassNames}
      data-anv="flex"
      style={styleCombined}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

Flex.displayName = "Flex";
